<template>
  <HomePage :welcomeMessage1="welcomeMessage1" :welcomeMessage2="welcomeMessage2" :messageParams="messageParams">
    <template v-for="(card, index) in homepageData">
      <HomePageCard
        v-if="card.display"
        :key="index"
        :title="$t(card.title)"
        :description="$t(card.description, card.descriptionParams)"
        :icon="card.icon"
        :iconBgColour="card.iconBgColour"
        :clickable="card.clickable"
        @click="card.event"
      />
    </template>
  </HomePage>
</template>
<script>
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapState } = createNamespacedHelpers('user')

export default {
  name: 'HomeView',
  data() {
    return {
      welcomeMessage1: 'homepage:welcome_message_line_1',
      welcomeMessage2: 'homepage:welcome_message_line_2',
      messageParams: { first_name: this.$store.state.user.userInfo.investor_first_name }
    }
  },
  computed: {
    ...mapState(['bootstrapLoaded']),
    // The helper is already namespaced to `user`, so these must not be
    // prefixed again -- 'user/isActiveClient' resolves to the nonexistent
    // getter user/user/isActiveClient.
    ...mapGetters(['clientMaintenanceEnabled', 'isActiveClient', 'isIPV2Enabled', 'isIPV3Enabled', 'opsEmail']),
    theme() {
      return this.$store.state.theme.theme
    },
    userFirstName() {
      return this.$store.state.user.userInfo.investor_first_name
    },
    /**
     * Built as a computed rather than in data() so the cards react to store
     * updates. `bootstrapCall` is awaited in the router's global beforeEach, so
     * the AppConfig flags are loaded before this view mounts, but firmDetails
     * is not: ContextService dispatches context/fetchFirmDetails from its
     * constructor without awaiting it. Evaluated once at creation, a card could
     * therefore be hidden forever despite a click having somewhere to go.
     */
    homepageData () {
      return [
        {
          title: 'homepage:personal_details_title',
          description: 'homepage:personal_details_description',
          icon: 'user',
          iconBgColour: '#E7EDD0',
          event: () => this.$router.push({ name: 'DcClientMaintenancePage' }),
          display: this.isActiveClient && this.clientMaintenanceEnabled
        },
        {
          title: 'homepage:your_files_title',
          description: 'homepage:your_files_description',
          icon: 'file',
          iconBgColour: '#DDD6E0',
          event: () => this.$router.push({ name: 'MyDocumentsPage' }),
          display: false // Disabled due to Lara request https://pas.tpondemand.com/restui/board.aspx?#page=feature/33781 -ET
        },
        {
          title: 'homepage:d1g1t_title',
          description: 'homepage:d1g1t_description',
          icon: 'chart-mixed',
          iconBgColour: '#DAD1D0',
          event: () => {
            const target = this.resolveInvestmentsTarget()

            if (!target) {
              console.warn('No valid link found.')
              return
            }

            if (target.newTab) {
              window.open(target.url, '_blank', 'noopener')
            } else {
              window.location.href = target.url
            }
          },
          display: !!this.resolveInvestmentsTarget()
        },
        this.needHelpCard()
      ]
    }
  },
  methods: {
    // Falls back to the firm's ops email (opsEmail getter) instead of a
    // personalized message when no advisor is assigned yet.
    needHelpCard () {
      const userInfo = this.$store.state.user.userInfo
      const hasAdvisor = !!userInfo.advisor_email

      return {
        title: 'homepage:need_help_title',
        description: hasAdvisor ? 'homepage:need_help_description' : 'homepage:need_help_description_generic',
        descriptionParams: hasAdvisor
          ? {
              first_name: userInfo.advisor_first_name,
              last_name: userInfo.advisor_last_name,
              email: userInfo.advisor_email
            }
          : { email: this.opsEmail },
        icon: 'circle-question',
        iconBgColour: '#E1DCD4',
        clickable: false,
        event: () => {},
        display: true
      }
    },
    /**
     * Resolve where the investments card should send the investor, newest
     * portal first: IPV3, then IPV2 (wealth OS), then d1g1t. Each portal needs
     * both its feature flag on and a link configured on the firm, so a firm
     * that has a link populated ahead of its flag being flipped still falls
     * through to the portal it is actually on. Returns null when nothing is
     * configured, which also hides the card.
     */
    resolveInvestmentsTarget () {
      const firmDetails = this.$store?.state?.context?.firmDetails

      if (this.isIPV3Enabled && firmDetails?.investor_portal_v3_link) {
        return { url: firmDetails.investor_portal_v3_link, newTab: false }
      }

      if (this.isIPV2Enabled && firmDetails?.investor_portal_v2_link) {
        return { url: firmDetails.investor_portal_v2_link, newTab: false }
      }

      if (firmDetails?.d1g1t_investor_link) {
        return { url: firmDetails.d1g1t_investor_link, newTab: true }
      }

      return null
    }
  }
}
</script>
